/**
 * profileRepo — Supabase + R2 access for km_user_profiles.
 *
 * Pages must NOT import from `@supabase/supabase-js` directly; everything
 * profile-related flows through here so the RLS contract and the upload
 * orchestration stay in one file.
 */

import { supabase } from '@/lib/supabase';
import { transcodeAvatarToWebp } from '@/lib/avatar';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

/**
 * Read the current user's own profile row (owner-RLS protected, includes
 * `metadata`). Returns null when no row exists yet — the profile is
 * lazily created on first save (see upsertMyProfile).
 */
export async function getMyProfile() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('km_user_profiles')
    .select('auth_user_id, display_name, pen_name, bio, avatar_object_key, metadata, created_at, updated_at')
    .eq('auth_user_id', user.id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Read a public profile by auth_user_id (for story-card attribution).
 * Goes through km_public_profiles, so `metadata` is never exposed.
 */
export async function getPublicProfile(authUserId) {
  if (!authUserId) return null;
  const { data, error } = await supabase
    .from('km_public_profiles')
    .select('auth_user_id, display_name, pen_name, bio, avatar_object_key, created_at')
    .eq('auth_user_id', authUserId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Insert-or-update the text fields of the current user's profile.
 * Only the columns passed in `fields` are written; others are untouched.
 *
 * Empty strings are normalised to null so the DB stays clean.
 */
export async function upsertMyProfile(fields) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');

  const payload = { auth_user_id: user.id };
  for (const key of ['display_name', 'pen_name', 'bio']) {
    if (key in fields) {
      const v = fields[key];
      payload[key] = typeof v === 'string' && v.trim() ? v.trim() : null;
    }
  }

  const { data, error } = await supabase
    .from('km_user_profiles')
    .upsert(payload, { onConflict: 'auth_user_id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Persist a new avatar_object_key on the profile (called immediately
 * after the R2 upload succeeds, separate from text-field saves).
 */
async function setAvatarObjectKey(objectKey) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');

  const { data, error } = await supabase
    .from('km_user_profiles')
    .upsert(
      { auth_user_id: user.id, avatar_object_key: objectKey },
      { onConflict: 'auth_user_id' },
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Core blob → R2 → profile pipeline. Shared by both the user-driven
 * upload (`uploadAvatar`) and the provider-avatar backfill
 * (`importProviderAvatar`).
 *
 *   1. Ask km-issue-upload-url for a short-lived presigned PUT URL.
 *   2. PUT the blob directly to R2.
 *   3. Persist the returned object_key on km_user_profiles.
 *
 * `contentType` must be one of the Edge Function's allowlisted MIMEs
 * (image/webp, image/jpeg, image/png) and must match the blob, or R2
 * will reject the signature.
 */
async function uploadAvatarBlob(blob, contentType) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');

  const presignRes = await fetch(`${SUPABASE_URL}/functions/v1/km-issue-upload-url`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ kind: 'avatar', content_type: contentType }),
  });
  if (!presignRes.ok) {
    const err = await presignRes.json().catch(() => ({ error: presignRes.statusText }));
    throw new Error(err.error || 'Could not get upload URL');
  }
  const { object_key, upload_url } = await presignRes.json();

  const putRes = await fetch(upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: blob,
  });
  if (!putRes.ok) {
    throw new Error(`Upload to storage failed (${putRes.status})`);
  }

  return setAvatarObjectKey(object_key);
}

/**
 * User-driven avatar upload: transcode the picked file to WebP and
 * push it through the shared pipeline. Returns the updated profile
 * row so the caller can re-render (new `updated_at` doubles as a
 * cache-buster on the <img>).
 */
export async function uploadAvatar(file) {
  const blob = await transcodeAvatarToWebp(file);
  return uploadAvatarBlob(blob, 'image/webp');
}

/**
 * One-shot import of the OAuth provider's avatar (Google etc.) into
 * R2. Used to bootstrap a profile so story-card attribution works
 * without forcing the user to upload anything.
 *
 * We deliberately skip the WebP transcode here:
 *   • Google's profile CDN already serves small, optimised JPEGs
 *     (~5–20 KB) — re-encoding gains almost nothing and would taint
 *     the canvas (cross-origin image data isn't readable).
 *   • Pushing the original blob through means the import works even
 *     if the browser blocks canvas reads on the source image.
 *
 * The blob's reported `type` is trusted only as far as the Edge
 * Function's allowlist (webp/jpeg/png) — anything else is rejected
 * upstream. If the provider hands us something exotic we fall back
 * to image/jpeg which covers the Google common case.
 */
export async function importProviderAvatar(providerUrl) {
  if (!providerUrl) throw new Error('No provider avatar URL');

  const res = await fetch(providerUrl, { mode: 'cors' });
  if (!res.ok) throw new Error(`Provider avatar fetch failed (${res.status})`);
  const blob = await res.blob();

  const ALLOWED = new Set(['image/webp', 'image/jpeg', 'image/png']);
  const contentType = ALLOWED.has(blob.type) ? blob.type : 'image/jpeg';

  return uploadAvatarBlob(blob, contentType);
}
